import {useState, useEffect, useCallback} from 'react'
import PropTypes from 'prop-types'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  useDisclosure,
} from '@chakra-ui/react'
import {useLocation, useNavigate} from 'react-router'
import useSWRMutation from 'swr/mutation'
import {
  LogoUploadModal,
  InvitationTypeModal,
  SelectPayersModal,
  CustomizeEmailTemplateModal,
  ConfirmationPageModal,
} from './modals'
import ExitConfirmationDialog from '../ExitConfirmationDialog'
import {useRails} from '../../contexts/rails'
import {fetcher, defaultHeaders} from '../../../api/fetcher'
import {refreshStepperData} from '../CashinStepper/useStepperData'

const payerInvitationSteps = [
  {
    step: 'logo_upload',
    previousStep: null,
  },
  {
    step: 'invitation_type',
    previousStep: 'logo_upload',
  },
  {
    step: 'select_customer',
    nextStep: 'customize_email_template',
    previousStep: 'invitation_type',
  },
  {
    step: 'select_customers',
    previousStep: 'invitation_type',
  },
  {
    step: 'customize_email_template',
    previousStep: 'select_customer',
  },
  {
    step: 'confirmation_page',
    previousStep: 'customize_email_template',
  },
]

const InvitePayerToMarmaladeModal = ({isOpen, hasLogo}) => {
  const {
    isOpen: isAlertOpen,
    onOpen: onAlertOpen,
    onClose: onAlertClose,
  } = useDisclosure()
  const location = useLocation()
  const navigate = useNavigate()
  const {
    organisation: {currentSupplier},
    authenticityToken,
  } = useRails()

  const supplierId = currentSupplier?.id

  const {trigger: beginTransition} = useSWRMutation(
    `/api/suppliers/${supplierId}/banking_details_transitions/begin`,
    async (url) => {
      return fetcher(url, {
        method: 'PUT',
        headers: defaultHeaders({authenticityToken}),
      })
    },
  )

  // Update step when search params change
  // Extract step from URL path: /customers/invite-to-marmalade/logo-upload -> 'logo-upload'
  const getCurrentStepFromPath = useCallback(() => {
    const pathParts = location.pathname.split('/')
    const lastPart = pathParts[pathParts.length - 1]

    // If the last part is 'transition_to_marmalade' (no step), default to logo_upload
    if (lastPart === 'transition_to_marmalade' || !lastPart) {
      return 'logo_upload'
    }

    // Convert URL format (kebab-case) to step format (snake_case)
    return lastPart.replace(/-/g, '_')
  }, [location.pathname])

  const [step, setStep] = useState(getCurrentStepFromPath())

  // Mark transition as started when modal opens
  useEffect(() => {
    if (isOpen && beginTransition) {
      beginTransition()
    }
  }, [isOpen, beginTransition])

  // Update step when path changes
  useEffect(() => {
    setStep(getCurrentStepFromPath())
  }, [location.pathname, getCurrentStepFromPath])

  // Get current step index
  const currentStepObj = payerInvitationSteps.find((s) => s.step === step)
  const isConfirmationStep = step === 'confirmation_page'

  const goToNextStep = (nextStep, state) => {
    const targetStep = nextStep || currentStepObj.nextStep
    if (targetStep) {
      navigate(`/customers/transition_to_marmalade/${targetStep}`, {
        state: {
          referrer: location.state?.referrer,
          ...state,
        },
      })
    }
  }

  const goToPreviousStep = (updatedState = {}) => {
    // Check if we have a dynamic previous step in the state (e.g., from select_payers/select_payer)
    const previousStep =
      location.state?.fromStep || currentStepObj?.previousStep

    if (previousStep) {
      const {fromStep: _fromStep, ...restState} = location.state || {}
      const newState = {
        ...restState,
        ...updatedState,
      }

      navigate(`/customers/transition_to_marmalade/${previousStep}`, {
        state: newState,
      })
    }
  }

  const goToUpdateTemplates = () => {
    navigate('/setup_templates')
  }

  const onRestart = () => {
    navigate(`/customers/transition_to_marmalade`, {
      state: {
        referrer: location.state?.referrer,
      },
    })
  }

  // Render step content
  const getModalStep = () => {
    switch (step) {
      case 'logo_upload':
        return <LogoUploadModal goToNextStep={goToNextStep} />
      case 'invitation_type':
        return (
          <InvitationTypeModal
            goToNextStep={goToNextStep}
            goToPreviousStep={goToPreviousStep}
          />
        )
      case 'select_customers':
        return (
          <SelectPayersModal
            invitationType="bulk"
            goToNextStep={goToNextStep}
            goToPreviousStep={goToPreviousStep}
          />
        )
      case 'select_customer':
        return (
          <SelectPayersModal
            invitationType="individual"
            goToNextStep={goToNextStep}
            goToPreviousStep={goToPreviousStep}
          />
        )
      case 'customize_email_template':
        return (
          <CustomizeEmailTemplateModal
            goToPreviousStep={goToPreviousStep}
            goToNextStep={goToNextStep}
          />
        )
      case 'confirmation_page':
        return (
          <ConfirmationPageModal
            onRestart={onRestart}
            goToUpdateTemplates={goToUpdateTemplates}
          />
        )
      default:
        if (hasLogo) {
          return (
            <InvitationTypeModal
              goToNextStep={goToNextStep}
              goToPreviousStep={goToPreviousStep}
            />
          )
        }
        return <LogoUploadModal goToNextStep={goToNextStep} />
    }
  }

  const modalStep = getModalStep()

  // Handle modal close with confirmation
  const handleModalClose = () => {
    if (isConfirmationStep) {
      // Refresh stepper data before navigating away
      refreshStepperData()
      const referrer = location.state?.referrer || '/dashboard'
      navigate(referrer)
    } else {
      onAlertOpen()
    }
  }

  const handleConfirmClose = () => {
    onAlertClose()
    // Refresh stepper data when user confirms exit
    refreshStepperData()
    const referrer = location.state?.referrer || '/dashboard'
    navigate(referrer)
  }

  return (
    <>
      <Modal
        size="5xl"
        isOpen={isOpen}
        onClose={handleModalClose}
        closeOnOverlayClick
      >
        <ModalOverlay />
        <ModalContent
          bg={step === 'confirmation_page' ? 'mld.neutral.200' : 'white'}
          borderRadius="lg"
          height="650px"
        >
          {modalStep}
        </ModalContent>
      </Modal>

      <ExitConfirmationDialog
        isOpen={isAlertOpen}
        onClose={onAlertClose}
        onConfirmExit={handleConfirmClose}
      />
    </>
  )
}

InvitePayerToMarmaladeModal.defaultProps = {
  hasLogo: false,
}

InvitePayerToMarmaladeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  hasLogo: PropTypes.bool,
}

export default InvitePayerToMarmaladeModal
